import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Plus, Trash2, TrendingUp, TrendingDown, Minus, Bell,
  Monitor, Smartphone, Globe, Loader2, RefreshCw, ArrowUp, ArrowDown,
  AlertTriangle, Target, BarChart3, MapPin
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { keywordService } from '../../services/keywordService';
import toast from 'react-hot-toast';

interface TrackedKeyword {
  id: string;
  keyword: string;
  currentPosition: number;
  previousPosition: number;
  bestPosition: number;
  searchVolume: number;
  url: string;
  device: 'desktop' | 'mobile' | 'both';
  location: string;
  lastChecked: string;
  history: { date: string; position: number }[];
}

interface PositionDistribution {
  top3: number;
  top10: number;
  top20: number;
  top50: number;
  top100: number;
  notRanking: number;
}

interface Alert {
  id: string;
  keywordId: string;
  keyword: string;
  type: 'improved' | 'dropped' | 'entered_top10' | 'left_top10';
  oldPosition: number;
  newPosition: number;
  date: string;
}

const RankTrackerPage: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [trackedKeywords, setTrackedKeywords] = useState<TrackedKeyword[]>([]);
  const [selectedKeyword, setSelectedKeyword] = useState<TrackedKeyword | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKeywords, setNewKeywords] = useState('');
  const [newLocation, setNewLocation] = useState('Brazil');
  const [newDevice, setNewDevice] = useState<'desktop' | 'mobile' | 'both'>('both');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [positionDistribution, setPositionDistribution] = useState<PositionDistribution>({
    top3: 0, top10: 0, top20: 0, top50: 0, top100: 0, notRanking: 0
  });
  const [avgPosition, setAvgPosition] = useState(0);
  const [filter, setFilter] = useState('all');

  // Load initial data
  useEffect(() => {
    loadTrackedKeywords();
  }, []);

  const loadTrackedKeywords = async () => {
    setIsLoading(true);
    try {
      const response = await keywordService.getTrackedKeywords();
      if (response.success && response.data) {
        const keywords = response.data;
        setTrackedKeywords(keywords);

        // Calculate distribution from real data
        const dist = {
          top3: keywords.filter((k: TrackedKeyword) => k.currentPosition <= 3).length,
          top10: keywords.filter((k: TrackedKeyword) => k.currentPosition > 3 && k.currentPosition <= 10).length,
          top20: keywords.filter((k: TrackedKeyword) => k.currentPosition > 10 && k.currentPosition <= 20).length,
          top50: keywords.filter((k: TrackedKeyword) => k.currentPosition > 20 && k.currentPosition <= 50).length,
          top100: keywords.filter((k: TrackedKeyword) => k.currentPosition > 50 && k.currentPosition <= 100).length,
          notRanking: keywords.filter((k: TrackedKeyword) => k.currentPosition > 100).length
        };
        setPositionDistribution(dist);

        // Calculate average
        if (keywords.length > 0) {
          const avg = keywords.reduce((sum: number, k: TrackedKeyword) => sum + k.currentPosition, 0) / keywords.length;
          setAvgPosition(Math.round(avg * 10) / 10);
        } else {
          setAvgPosition(0);
        }

        // Load alerts
        try {
          const alertsResponse = await keywordService.getRankingAlerts();
          if (alertsResponse.success && alertsResponse.data) {
            setAlerts(alertsResponse.data);
          } else {
            setAlerts([]);
          }
        } catch {
          setAlerts([]);
        }
      } else {
        setTrackedKeywords([]);
        setPositionDistribution({ top3: 0, top10: 0, top20: 0, top50: 0, top100: 0, notRanking: 0 });
        setAvgPosition(0);
        setAlerts([]);
      }
    } catch (error) {
      console.error('Error loading tracked keywords:', error);
      setTrackedKeywords([]);
      setPositionDistribution({ top3: 0, top10: 0, top20: 0, top50: 0, top100: 0, notRanking: 0 });
      setAvgPosition(0);
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await keywordService.refreshRankings();
      toast.success(t('rankTracker.refreshStarted', 'Refresh started. This may take a few minutes.'));
      loadTrackedKeywords();
    } catch (error) {
      console.error('Error refreshing rankings:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddKeywords = async () => {
    if (!newKeywords.trim()) return;

    const keywords = newKeywords.split('\n').filter(k => k.trim());
    if (keywords.length === 0) return;

    try {
      await keywordService.addTrackedKeywords(keywords.map(k => ({
        keyword: k.trim(),
        device: newDevice,
        location: newLocation
      })));
      toast.success(t('rankTracker.keywordsAdded', '{{count}} keywords added', { count: keywords.length }));
      setNewKeywords('');
      setShowAddModal(false);
      loadTrackedKeywords();
    } catch (error) {
      console.error('Error adding keywords:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleDeleteKeyword = async (id: string) => {
    try {
      await keywordService.deleteTrackedKeyword(id);
      setTrackedKeywords(prev => prev.filter(k => k.id !== id));
      if (selectedKeyword?.id === id) {
        setSelectedKeyword(null);
      }
      toast.success(t('common.deleted', 'Excluído'));
    } catch (error) {
      console.error('Error deleting keyword:', error);
      toast.error(t('errors.generic'));
    }
  };

  const getPositionChange = (current: number, previous: number) => {
    const change = previous - current;
    if (change > 0) return { direction: 'up', value: change, color: 'text-green-600 dark:text-green-400' };
    if (change < 0) return { direction: 'down', value: Math.abs(change), color: 'text-red-600 dark:text-red-400' };
    return { direction: 'same', value: 0, color: 'text-gray-500 dark:text-gray-400' };
  };

  const getPositionBadgeVariant = (position: number): 'success' | 'warning' | 'danger' | 'gray' => {
    if (position <= 3) return 'success';
    if (position <= 10) return 'warning';
    if (position <= 50) return 'danger';
    return 'gray';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const filteredKeywords = trackedKeywords.filter(k => {
    if (filter === 'all') return true;
    if (filter === 'top3') return k.currentPosition <= 3;
    if (filter === 'top10') return k.currentPosition <= 10;
    if (filter === 'improved') return k.currentPosition < k.previousPosition;
    if (filter === 'dropped') return k.currentPosition > k.previousPosition;
    return true;
  });

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'improved':
        return <ArrowUp className="w-4 h-4 text-green-500" />;
      case 'dropped':
        return <ArrowDown className="w-4 h-4 text-red-500" />;
      case 'entered_top10':
        return <TrendingUp className="w-4 h-4 text-blue-500" />;
      case 'left_top10':
        return <TrendingDown className="w-4 h-4 text-orange-500" />;
    }
  };

  const getAlertText = (alert: Alert) => {
    switch (alert.type) {
      case 'improved':
        return t('rankTracker.alertImproved', 'moved up from #{{old}} to #{{new}}', { old: alert.oldPosition, new: alert.newPosition });
      case 'dropped':
        return t('rankTracker.alertDropped', 'dropped from #{{old}} to #{{new}}', { old: alert.oldPosition, new: alert.newPosition });
      case 'entered_top10':
        return t('rankTracker.alertEnteredTop10', 'entered Top 10 (#{{pos}})', { pos: alert.newPosition });
      case 'left_top10':
        return t('rankTracker.alertLeftTop10', 'left Top 10 (#{{pos}})', { pos: alert.newPosition });
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('nav.sections.rankTracker', 'Posição no Google')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {t('competitors.rankTrackerDescription', 'Monitor your keyword positions on Google')}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label={t('common.refresh', 'Refresh')}
            className="h-10 whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{t('common.refresh', 'Refresh')}</span>
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="h-10 whitespace-nowrap">
            <Plus className="w-4 h-4 mr-2" />
            <span className="sm:hidden">{t('common.add', 'Adicionar')}</span>
            <span className="hidden sm:inline">{t('rankTracker.addKeywords', 'Add Keywords')}</span>
          </Button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('common.loading')}
            </p>
          </div>
        </div>
      )}

      {!isLoading && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('rankTracker.trackedKeywords', 'Tracked Keywords')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {trackedKeywords.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('rankTracker.avgPosition', 'Avg Position')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      #{avgPosition}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('rankTracker.inTop10', 'In Top 10')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {positionDistribution.top3 + positionDistribution.top10}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Bell className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('rankTracker.alertsToday', 'Alerts Today')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {alerts.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Position Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>
                {t('rankTracker.positionDistribution', 'Position Distribution')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
                {[
                  { label: 'Top 3', value: positionDistribution.top3, color: 'bg-green-500' },
                  { label: 'Top 4-10', value: positionDistribution.top10, color: 'bg-blue-500' },
                  { label: 'Top 11-20', value: positionDistribution.top20, color: 'bg-yellow-500' },
                  { label: 'Top 21-50', value: positionDistribution.top50, color: 'bg-orange-500' },
                  { label: 'Top 51-100', value: positionDistribution.top100, color: 'bg-red-500' },
                  { label: t('rankTracker.notRanking', 'Not Ranking'), value: positionDistribution.notRanking, color: 'bg-gray-500' }
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className={`${item.color} text-white text-base sm:text-lg font-bold py-2 sm:py-4 rounded-lg mb-1 sm:mb-2`}>
                      {item.value}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{item.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-6">
            {/* Keywords Table */}
            <div className="xl:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
                    <CardTitle>
                      {t('rankTracker.keywordsList', 'Tracked Keywords')}
                    </CardTitle>
                    <Select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="w-full sm:w-40"
                      options={[
                        { value: 'all', label: t('common.all', 'All') },
                        { value: 'top3', label: 'Top 3' },
                        { value: 'top10', label: 'Top 10' },
                        { value: 'improved', label: t('rankTracker.improved', 'Improved') },
                        { value: 'dropped', label: t('rankTracker.dropped', 'Dropped') }
                      ]}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('keywords.keyword')}
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('keywords.position')}
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('rankTracker.change', 'Change')}
                          </th>
                          <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('keywords.searchVolume')}
                          </th>
                          <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('rankTracker.deviceLabel', 'Device')}
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredKeywords.map((keyword) => {
                          const change = getPositionChange(keyword.currentPosition, keyword.previousPosition);
                          return (
                            <tr
                              key={keyword.id}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${selectedKeyword?.id === keyword.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                              onClick={() => setSelectedKeyword(keyword)}
                            >
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white break-words">
                                    {keyword.keyword}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {keyword.location}
                                  </p>
                                </div>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <Badge variant={getPositionBadgeVariant(keyword.currentPosition)}>
                                  #{keyword.currentPosition}
                                </Badge>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <span className={`flex items-center gap-1 font-medium ${change.color}`}>
                                  {change.direction === 'up' && <ArrowUp className="w-4 h-4" />}
                                  {change.direction === 'down' && <ArrowDown className="w-4 h-4" />}
                                  {change.direction === 'same' && <Minus className="w-4 h-4" />}
                                  {change.value > 0 && change.value}
                                </span>
                              </td>
                              <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">
                                {formatNumber(keyword.searchVolume)}
                              </td>
                              <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3">
                                <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                  {keyword.device === 'desktop' && <Monitor className="w-4 h-4" />}
                                  {keyword.device === 'mobile' && <Smartphone className="w-4 h-4" />}
                                  {keyword.device === 'both' && (
                                    <>
                                      <Monitor className="w-4 h-4" />
                                      <Smartphone className="w-4 h-4" />
                                    </>
                                  )}
                                </span>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteKeyword(keyword.id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {filteredKeywords.length === 0 && (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">
                        {t('rankTracker.noKeywords', 'No keywords found')}
                      </p>
                      {trackedKeywords.length === 0 && (
                        <>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                            {t('rankTracker.emptyHint', 'Adicione as palavras que seus clientes digitam no Google (ex.: "salão de beleza em Campinas") e acompanhe sua posição todo dia.')}
                          </p>
                          <Button onClick={() => setShowAddModal(true)} className="mt-4 h-10">
                            <Plus className="w-4 h-4 mr-2" />
                            {t('rankTracker.addKeywords', 'Add Keywords')}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar: Selected Keyword Details + Alerts */}
            <div className="space-y-3 sm:space-y-6">
              {/* Selected Keyword Chart */}
              {selectedKeyword && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {selectedKeyword.keyword}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">{t('rankTracker.currentPosition', 'Current Position')}</p>
                          <p className="font-bold text-lg">#{selectedKeyword.currentPosition}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">{t('rankTracker.bestPosition', 'Best Position')}</p>
                          <p className="font-bold text-lg text-green-600">#{selectedKeyword.bestPosition}</p>
                        </div>
                      </div>

                      <ResponsiveContainer width="100%" height={150}>
                        <AreaChart data={selectedKeyword.history}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                            interval={6}
                          />
                          <YAxis
                            reversed
                            domain={[1, 'dataMax']}
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip
                            labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR')}
                            formatter={(v: number) => [`#${v}`, t('keywords.position')]}
                          />
                          <Area
                            type="monotone"
                            dataKey="position"
                            stroke="#3B82F6"
                            fill="#3B82F6"
                            fillOpacity={0.2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>

                      {selectedKeyword.url && (
                        <div className="text-sm">
                          <p className="text-gray-500 dark:text-gray-400">{t('rankTracker.rankingUrl', 'Ranking URL')}</p>
                          <a
                            href={selectedKeyword.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline truncate block"
                          >
                            {selectedKeyword.url}
                          </a>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Alerts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    {t('rankTracker.recentAlerts', 'Recent Alerts')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {alerts.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        {t('rankTracker.noAlerts', 'No recent alerts')}
                      </p>
                    ) : (
                      alerts.map((alert) => (
                        <div
                          key={alert.id}
                          className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          {getAlertIcon(alert.type)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                              {alert.keyword}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {getAlertText(alert)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {new Date(alert.date).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Add Keywords Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('rankTracker.addKeywords', 'Add Keywords')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('rankTracker.keywordsLabel', 'Keywords')}
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={5}
              placeholder={t('rankTracker.keywordsPlaceholder', 'Enter one keyword per line...')}
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('rankTracker.keywordsHint', 'One keyword per line')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('rankTracker.location', 'Location')}
              </label>
              <Select
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                options={[
                  { value: 'Brazil', label: 'Brasil' },
                  { value: 'United States', label: 'Estados Unidos' },
                  { value: 'Portugal', label: 'Portugal' },
                  { value: 'Mexico', label: 'Mexico' }
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('rankTracker.deviceLabel', 'Device')}
              </label>
              <Select
                value={newDevice}
                onChange={(e) => setNewDevice(e.target.value as 'desktop' | 'mobile' | 'both')}
                options={[
                  { value: 'both', label: t('rankTracker.bothDevices', 'Both') },
                  { value: 'desktop', label: 'Desktop' },
                  { value: 'mobile', label: 'Mobile' }
                ]}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddKeywords} disabled={!newKeywords.trim()}>
              <Plus className="w-4 h-4 mr-2" />
              {t('common.add', 'Add')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RankTrackerPage;
